import ClubExam from '../club/ClubExam.js';
import ClubSalesListOfferingItem from '../ClubSalesListOfferingItem.js'
import Department from '../Department.js';
import Instructor from './Instructor.js';
import OfferingAppointment from './OfferingAppointmentSeries.js';
import OfferingField from './OfferingField.js';
import OfferingPrice from './OfferingPrice.js';
import Sport from './Sport.js';
import Team from '../Team.js';

export default class Offering {

    static STATUS_PUBLISHED = 'published';
    static STATUS_DRAFT = 'draft';

    static BOOKING_TYPE_PER_APPOINTMENT = 'perAppointment';
    static BOOKING_TYPE_PER_OFFERING = 'perOffering';

    static BOOKING_MODE_OFFERING = 'offering';
    static BOOKING_MODE_APPOINTMENT = 'appointment';

    static CONFIRMATION_TYPE_ACCEPTANCE_REQUIRED = 'acceptance_required';
    static CONFIRMATION_TYPE_DECLINATION_REQUIRED = 'declination_required';

    static EXECUTION_TYPE_HYBRID = 'hybrid';
    static EXECUTION_TYPE_ONLINE = 'online';
    static EXECUTION_TYPE_PRESENCE = 'presence';

    static OFFERING_CATEGORY_COURSE = 'course';
    static OFFERING_CATEGORY_EXAM = 'exam';
    static OFFERING_CATEGORY_TRAINING = 'training';

    static PRICING_FREE = 'free';
    static PRICING_CHARGEBLE = 'chargeble';

    constructor()
    {
        this.bookableForActiveMembers = true;
        this.bookableForPassiveMembers = false;
        this.bookableForGuestMembers = false;
        this.bookableForWomen = true;
        this.bookableForMen = true;
        this.bookableForAdults = true;
        this.bookableForNonAdults = true;
        this.registrationRequested = false;
        this.takesPlaceOnPublicHoliday = false;
        this.takesPlaceInSchoolHolidays = false;
        this.showAvailablePlaces = false;
        this.bookingStart = null;
        this.bookingEnd = null;
        this.cancellableUntilDate = null;
        this.cancellableUntilAmount = null;
        this.cancellableUntilType = null;
        this.minParticipants = null;
        this.maxParticipants = null;
        this.minimumAge = null;
        this.prices = [];
        this.executionType = Offering.EXECUTION_TYPE_PRESENCE;
        this.bookingType = Offering.BOOKING_TYPE_PER_OFFERING;
        this.appointmentSeriesList = [];
        this.onlineUrl = '';
        this.sport = {};
        this.offeringCategory = {};
        this.status = Offering.STATUS_DRAFT;
        this.bookingMode = Offering.BOOKING_MODE_APPOINTMENT;
        this.hasActiveBookings = false;
        this.hasUncancelledBookings = false;
        this.registrationRequested = true;
        this.fields = [];
        this.useWaitList = false;
        this.reservationTimeInMinutes = 90;
        this.order = 'sequential';
        this.isArchivable = false;
        this.archived = false;
        this.accessibleForTeamMembersOnly = false;
        this.showCommentField = false;
        this.commentFieldCaption = 'Möchtest du uns etwas mitteilen?';
        this.confirmationType = Offering.CONFIRMATION_TYPE_ACCEPTANCE_REQUIRED;
        this.clubExams = [];
        this.selectableExams = false;
        this.imageUrl = null;
        this.salesListOfferingItems = [];
    }

    static fromData(data) {
        if (!data) {
            return;
        }

        let offering = new Offering();

        offering.id = data.id || null;
        offering.title = data.title;
        offering.description = data.description;
        offering.requirements = data.requirements;
        offering.minParticipants = parseInt(data.minParticipants) || null;
        offering.maxParticipants = parseInt(data.maxParticipants) || null;
        offering.minimumAge = parseInt(data.minimumAge) || null;
        offering.maximumAge = parseInt(data.maximumAge) || null;
        offering.bookingType = data.bookingType || null;
        offering.bookingStart = data.bookingStart;
        offering.bookingEnd = data.bookingEnd;
        offering.bookingStartPerAppointmentAmount = data.bookingStartPerAppointmentAmount || null;
        offering.bookingStartPerAppointmentType = data.bookingStartPerAppointmentType || null;
        offering.bookingEndPerAppointmentAmount = data.bookingEndPerAppointmentAmount || null;
        offering.bookingEndPerAppointmentType = data.bookingEndPerAppointmentType || null;
        offering.cancellableUntilDate = data.cancellableUntilDate || null;
        offering.cancellableUntilAmount = data.cancellableUntilAmount || null;
        offering.cancellableUntilType = data.cancellableUntilType || null;
        offering.customId = data.customId;
        offering.isBookable = data.isBookable;
        offering.bookableForDepartments = data.bookableForDepartments || false;
        offering.bookableForTeams = data.bookableForTeams || false;
        offering.bookableForActiveMembers = data.bookableForActiveMembers;
        offering.bookableForPassiveMembers = data.bookableForPassiveMembers || false;
        offering.bookableForGuestMembers = data.bookableForGuestMembers || false;
        offering.bookableForMen = data.bookableForMen;
        offering.bookableForWomen = data.bookableForWomen;
        offering.bookableForNonAdults = data.bookableForNonAdults;
        offering.bookableForAdults = data.bookableForAdults;
        offering.registrationRequested = data.registrationRequested || false;
        offering.showAvailablePlaces = data.showAvailablePlaces || false;
        offering.showPrices = data.showPrices || false;
        offering.status = data.status || null;
        offering.takesPlaceOnPublicHoliday = data.takesPlaceOnPublicHoliday || false;
        offering.takesPlaceInSchoolHolidays = data.takesPlaceInSchoolHolidays || false;
        if (data.prices) {
            offering.prices = data.prices.map(data => OfferingPrice.fromData(data));
        }
        offering.bookingMode = data.bookingMode;
        offering.hasActiveBookings = data.hasActiveBookings || false;
        offering.hasUncancelledBookings = data.hasUncancelledBookings || false;
        offering.offeringCategory = data.offeringCategory;
        offering.sport = Sport.fromData(data.sport);
        offering.departments = data.departments.map(data => Department.fromData(data));
        offering.teams = data.teams.map(data => Team.fromData(data));
        offering.instructors = data.instructors.map(data => Instructor.fromData(data));
        offering.fields = data.fields.map(data => OfferingField.fromData(data));
        offering.appointmentSeriesList = data.appointmentSeriesList.map(data => OfferingAppointment.fromData(data));
        offering.isEditable = data.isEditable;
        offering.isDeletable = data.isDeletable;
        offering.isArchivable = data.isArchivable;
        offering.archived = data.archived;
        offering.nextAppointment = data.nextAppointment;
        offering.executionType = data.executionType;
        offering.onlineUrl = data.onlineUrl;
        offering.useWaitList = data.useWaitList || false;
        offering.hasMembersOnWaitList = data.hasMembersOnWaitList || false;
        offering.reservationTimeInMinutes = data.reservationTimeInMinutes || 90;
        offering.order = data.order || 'sequential';
        offering.accessibleForTeamMembersOnly = data.accessibleForTeamMembersOnly || false;
        offering.showCommentField = data.showCommentField;
        offering.commentFieldCaption = data.commentFieldCaption;
        offering.confirmationType = data.confirmationType || Offering.CONFIRMATION_TYPE_ACCEPTANCE_REQUIRED;
        if (data.clubExams) {
            offering.clubExams = data.clubExams.map(clubExam => ClubExam.fromData(clubExam));
        }
        if (data.selectableExams) {
            offering.selectableExams = data.selectableExams;
        }
        offering.imageUrl = data.imageUrl;
        offering.canEditClubExam = data.canEditClubExam;
        offering.canViewDogs = data.canViewDogs;
        offering.canCreateOfferingBookings = data.canCreateOfferingBookings;
        offering.canEditSalesListOfferingItems = data.canEditSalesListOfferingItems || false;
        offering.salesListOfferingItems = data.salesListOfferingItems ? data.salesListOfferingItems.data.map(offeringItem => ClubSalesListOfferingItem.fromData(offeringItem)) : [];

        return offering;
    }

    static duplicate(reference) {
        if (!reference) {
            return;
        }

        let offering = new Offering();
        offering.appointmentSeriesList = reference.appointmentSeriesList.map(data => OfferingAppointment.fromData(data, true));
        offering.bookableForActiveMembers = reference.bookableForActiveMembers;
        offering.bookableForPassiveMembers = reference.bookableForPassiveMembers;
        offering.bookableForGuestMembers = reference.bookableForGuestMembers;
        offering.bookableForAdults = reference.bookableForAdults;
        offering.bookableForMen = reference.bookableForMen;
        offering.bookableForNonAdults = reference.bookableForNonAdults;
        offering.bookableForWomen = reference.bookableForWomen;
        offering.bookingEnd = reference.bookingEnd;
        offering.bookingEndPerAppointmentAmount = reference.bookingEndPerAppointmentAmount;
        offering.bookingEndPerAppointmentType = reference.bookingEndPerAppointmentType;
        offering.bookingStart = reference.bookingStart;
        offering.bookingStartPerAppointmentAmount = reference.bookingStartPerAppointmentAmount;
        offering.bookingStartPerAppointmentType = reference.bookingStartPerAppointmentType;
        offering.cancellableUntilDate = reference.cancellableUntilDate;
        offering.cancellableUntilAmount = reference.cancellableUntilAmount;
        offering.cancellableUntilType = reference.cancellableUntilType;
        offering.bookingType = reference.bookingType;
        offering.customId = null;
        offering.departments = reference.departments;
        offering.description = reference.description;
        offering.executionType = reference.executionType;
        offering.fields = reference.fields;
        offering.id = null;
        offering.instructors = reference.instructors;
        offering.isDeletable = true;
        offering.maxParticipants = reference.maxParticipants;
        offering.maximumAge = reference.maximumAge;
        offering.minParticipants = reference.minParticipants;
        offering.minimumAge = reference.minimumAge;
        offering.offeringCategory = reference.offeringCategory;
        offering.onlineUrl = reference.onlineUrl;
        if (reference.prices) {
            offering.prices = reference.prices.map(data => OfferingPrice.fromData(data, true));
        }
        offering.registrationRequested = reference.registrationRequested;
        offering.requirements = reference.requirements;
        offering.showAvailablePlaces = reference.showAvailablePlaces;
        offering.sport = reference.sport;
        offering.status = Offering.STATUS_DRAFT;
        offering.takesPlaceInSchoolHolidays = reference.takesPlaceInSchoolHolidays;
        offering.takesPlaceOnPublicHoliday = reference.takesPlaceOnPublicHoliday;
        offering.teams = reference.teams;
        offering.title = reference.title;
        offering.bookingMode = reference.bookingMode;

        offering.useWaitList = reference.useWaitList;
        offering.reservationTimeInMinutes = reference.reservationTimeInMinutes;
        offering.order = reference.order;
        offering.showCommentField = reference.showCommentField;
        offering.commentFieldCaption = reference.commentFieldCaption;
        offering.confirmationType = reference.confirmationType;
        if (offering.clubExams) {
            offering.clubExams = reference.clubExams;
        }
        offering.selectableExams = reference.selectableExams;
        offering.salesListOfferingItems = reference.salesListOfferingItems;
        offering.referenceId = reference.id;

        return offering;
    }

    equals(offering) {
        if (offering.id === null) {
            return false;
        }
        return offering.id === this.id;
    }

    isBookingModeOffering() {
        return this.bookingMode === Offering.BOOKING_MODE_OFFERING;
    }

    isBookingModeAppointment() {
        return this.bookingMode === Offering.BOOKING_MODE_APPOINTMENT;
    }

    isPublished() {
        return this.status === Offering.STATUS_PUBLISHED;
    }

    isTraining() {
        return this.offeringCategory.name === Offering.OFFERING_CATEGORY_TRAINING;
    }

    isExam() {
        return this.offeringCategory.name === Offering.OFFERING_CATEGORY_EXAM;
    }

    isExamForDogs() {
        return this.isExam() && this.clubExams && this.clubExams.filter(clubExam => clubExam.isForDogs()).length > 0;
    }

    isDeclinationRequired() {
        return this.confirmationType === Offering.CONFIRMATION_TYPE_DECLINATION_REQUIRED;
    }

    checkParticipants() {
        if (this.minParticipants === '') {
            this.minParticipants = null;
        }
        if (this.maxParticipants === '') {
            this.maxParticipants = null;
        }
        if (this.minParticipants !== null) {
            this.minParticipants = parseInt(this.minParticipants);
        }
        if (this.maxParticipants !== null) {
            this.maxParticipants = parseInt(this.maxParticipants);

            if (this.maxParticipants === 0) {
                return 'offering.error.maximumParticipantsMustBeGreaterThanZero';
            } else if (this.minParticipants !== null && this.maxParticipants < this.minParticipants) {
                return 'offering.error.maximumParticipantsMustBeGreaterOrEqualThanMinimumParticipants';
            }
        }
        return null;
    }

    hasIntervalEndForAppointmentSeries() {
        return this.appointmentSeriesList.filter(appointmentSeries => appointmentSeries.intervalEnd === null).length === 0;
    }

    serialize() {
        const offering = Object.assign({}, this);
        offering.offeringCategoryId = offering.offeringCategory.id;
        delete offering.offeringCategory;
        offering.sportId = offering.sport.id;
        delete offering.sport;
        if (this.reservationTimeInMinutes && this.reservationTimeInMinutes.value) {
            this.reservationTimeInMinutes = this.reservationTimeInMinutes.value;
        }
        if (this.order && this.order.value) {
            this.order = this.order.value;
        }

        return offering;
    }
}
